import React, { useState, useEffect, useRef } from 'react';
import { Drawer, Tabs, Tab, Box, Button, TextField, FormControlLabel, Checkbox, IconButton, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MoreHorizOutlined } from '@mui/icons-material';
import WorkflowSessionModal from '../../../ItemProperties/WorkflowSessionModal';
import MqSeriesCustomHeaders from './MqSeriesCustomHeaders';
import { definedHeaderFields } from '../../../../constants/constantList';

const MqSeriesMessageHeaders = ({ open, onClose, headerFields = {}, customHeaders = '', onSave }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [fields, setFields] = useState(headerFields);
  const [correlationIdHex, setCorrelationIdHex] = useState(fields.CORRELATION_ID_HEX === 'true');
  const [messageIdHex, setMessageIdHex] = useState(fields.MESSAGE_ID_HEX === 'true');
  const [customHeaderFields, setCustomHeaderFields] = useState(customHeaders || '');

  // WorkflowSessionModal state
  const [workflowSessionModal, setWorkflowSessionModal] = useState({ open: false, isFirstTime: false });
  const [workflowSessionField, setWorkflowSessionField] = useState('');

  // Add ref for MqSeriesCustomHeaders
  const customHeadersRef = useRef();

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleFieldChange = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const handleWorkflowSessionModalOpen = (fieldKey) => {
    setWorkflowSessionField(fieldKey);
    setWorkflowSessionModal({ open: true, isFirstTime: true });
  };

  const handleWorkflowSessionModalClose = () => {
    setWorkflowSessionModal({ open: false, isFirstTime: false });
  };

  const handleWorkflowSessionModalValue = (value) => {
    handleFieldChange(workflowSessionField, value);
  };

  const handleCancel = () => {
    // Clear all fields
    setFields({});
    setCorrelationIdHex(false);
    setMessageIdHex(false);
    setCustomHeaderFields('');
    onClose();
  };

  const handleDone = () => {
    // Get processed custom headers data from the ref
    const processedCustomHeaders = customHeadersRef.current?.getProcessedData() || customHeaderFields;
    onSave(fields, correlationIdHex, messageIdHex, processedCustomHeaders);
  };
  
  return (
    <>
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{ width: 600, flexShrink: 0, '& .MuiDrawer-paper': { width: 800, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', height: '100%' } }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 2 }}>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label={t('DIAG_MQ_DEFINED_HEADER_FIELDS_TIT') || 'Defined Header Fields'} />
            <Tab label={t('DIAG_MQ_CUSTOM_HEADER_FIELDS_TIT') || 'Custom Header Fields'} />
          </Tabs>
        </Box>
        <Box sx={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0, p: 2 }}>
          {tab === 0 && (
            <Grid container spacing={2}>
              {definedHeaderFields.map(field => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t(field.label)}
                      value={fields[field.key] || ''}
                      onChange={e => handleFieldChange(field.key, e.target.value || null)}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ width: '40px', minWidth: '40px' }}
                      onClick={() => handleWorkflowSessionModalOpen(field.key)}
                    >
                      <MoreHorizOutlined fontSize="small" />
                    </Button>
                    {field.hex && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.key === 'CORRELATION_ID' ? correlationIdHex : messageIdHex}
                            onChange={e => {
                              if (field.key === 'CORRELATION_ID') setCorrelationIdHex(e.target.checked);
                              if (field.key === 'MESSAGE_ID') setMessageIdHex(e.target.checked);
                            }}
                          />
                        }
                        label={t('BTN_IS_HEX')}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
          {tab === 1 && (
            <MqSeriesCustomHeaders
              ref={customHeadersRef}
              customHeaders={customHeaderFields}
              setCustomHeaders={setCustomHeaderFields}
              onSave={(value) => setCustomHeaderFields(value)}
            />
          )}
        </Box>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, flexShrink: 0 }}>
          <Button variant="outlined" onClick={handleCancel}>{t('CANCEL')}</Button>
          <Button variant="contained" onClick={handleDone}>{t('BTN_DONE')}</Button>
        </Box>
      </Drawer>
      <WorkflowSessionModal
        valueLabel={t(definedHeaderFields.find(f => f.key === workflowSessionField)?.label) || ''}
        showAccountData={true}
        showCallingWorkflow={true}
        showSystemData={true}
        showWorkflowSession={true}
        showDateAndTime={true}
        openModal={workflowSessionModal}
        setModalOpen={setWorkflowSessionModal}
        handleClose={handleWorkflowSessionModalClose}
        initialValue={fields[workflowSessionField] || ''}
        inputType={['LBL_WS_PASSWORD', 'LBL_PASSWORD'].includes(workflowSessionField) ? 'password' : 'text'}
        valuesHandler={handleWorkflowSessionModalValue}
      />
    </>
  );
};

export default MqSeriesMessageHeaders; 